import { useState } from 'react';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '@/config/constants';
import { SparklesIcon } from '@heroicons/react/24/outline';

interface AIGenerateButtonProps {
  onContentGenerated: (content: string) => void;
  type: 'excerpt' | 'title' | 'seo';
  title?: string;
  content?: string;
  category?: string;
  location?: string;
  className?: string;
  size?: 'sm' | 'md';
}

const AIGenerateButton = ({
  onContentGenerated,
  type,
  title,
  content,
  category,
  location,
  className = '',
  size = 'sm',
}: AIGenerateButtonProps) => {
  const [loading, setLoading] = useState(false);

  const generateContent = async () => {
    try {
      setLoading(true);

      const token = getAuthToken();
      let prompt = '';

      switch (type) {
        case 'excerpt':
          if (content) {
            prompt = `Create a compelling excerpt for this blog post: ${content.substring(0, 200)}...`;
          } else if (title) {
            prompt = `Create a compelling excerpt for a blog post titled: ${title}`;
          } else {
            prompt = 'Create a compelling excerpt for this blog post';
          }
          break;
        case 'title':
          prompt = 'Generate an engaging, SEO-friendly blog post title';
          if (category) prompt += ` for the ${category} category`;
          if (location) prompt += ` about ${location}`;
          break;
        case 'seo':
          if (title) {
            prompt = `Create an SEO meta description for: ${title}`;
          } else {
            prompt = 'Create an SEO meta description for this blog post';
          }
          break;
      }

      const response = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          prompt,
          type,
          context: { title, category, location },
        }),
      });

      const result = await response.json();

      if (result.success) {
        const content = result.data?.content || result.data;
        onContentGenerated(content);
      }
    } catch (error) {
      console.error('AI generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  const sizeClasses = size === 'sm' ? 'px-2 py-1 text-xs' : 'px-3 py-2 text-sm';

  return (
    <button
      type="button"
      onClick={generateContent}
      disabled={loading}
      className={`inline-flex items-center space-x-1 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 transition-colors ${sizeClasses} ${className}`}
    >
      {loading ? (
        <div className="animate-spin rounded-full h-3 w-3 border border-white border-t-transparent"></div>
      ) : (
        <SparklesIcon className="h-3 w-3" />
      )}
      <span>{loading ? 'Generating...' : `AI ${type}`}</span>
    </button>
  );
};

export default AIGenerateButton;
